<template>
  <div v-if="minimizedChatsStore.chats.length > 0" class="minimized-chats-container">
    <TransitionGroup name="bubble">
      <div
        v-for="chat in minimizedChatsStore.chats"
        :key="chat.conversationId"
        class="minimized-chat-bubble"
        @click="$emit('restore', chat.conversationId)"
      >
        <v-avatar :size="48"
          :color="getConvColor(chat.conversation)"
          style="font-size: 16px; font-weight: 700; color: white;">
          <v-img v-if="getAvatar(chat.conversation)" :src="getAvatar(chat.conversation)!" cover />
          <span v-else-if="getConvInitials(chat.conversation)">{{ getConvInitials(chat.conversation) }}</span>
          <v-icon v-else :icon="getIcon(chat.conversation)" size="24" />
        </v-avatar>

        <!-- Unread badge -->
        <div v-if="chat.unreadCount > 0" class="minimized-badge">
          {{ chat.unreadCount > 99 ? '99+' : chat.unreadCount }}
        </div>

        <!-- Close button -->
        <div class="minimized-close" @click.stop="minimizedChatsStore.restore(chat.conversationId)">
          <v-icon icon="mdi-close" size="12" />
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { useMinimizedChatsStore } from '@/stores/chat/minimizedChatsStore';
import { useUserStore } from '@/stores/user/userStore';
import { getUserAvatarUrl, getTeamImage } from '@/utils/imageUtils';
import { getAvatarColor } from '@/utils/userUtils';
import type { IConversation } from '@/stores/chat/chatStore';

defineEmits<{
  (e: 'restore', conversationId: string): void;
}>();

const minimizedChatsStore = useMinimizedChatsStore();
const userStore = useUserStore();

function getAvatar(conversation: IConversation): string | null {
  if (conversation.type === 'direct') {
    const otherMember = conversation.members.find(m => {
      const id = typeof m.user === 'object' ? m.user._id : m.user;
      return id !== userStore.getUserId;
    })?.user;
    if (otherMember) return getUserAvatarUrl(otherMember);
  }
  if (conversation.type === 'team' && conversation.avatar) {
    return getTeamImage(conversation.avatar, 'w75');
  }
  return null;
}

function getIcon(conversation: IConversation): string {
  if (conversation.type === 'team') return 'mdi-shield-account';
  if (conversation.type === 'group') return 'mdi-account-group';
  return 'mdi-account';
}

function getOtherUser(conversation: IConversation) {
  if (conversation.type !== 'direct') return null;
  const other = conversation.members.find(m => {
    const id = typeof m.user === 'object' ? m.user._id : m.user;
    return id !== userStore.getUserId;
  })?.user;
  return other && typeof other === 'object' ? other as any : null;
}

function getConvInitials(conversation: IConversation): string {
  const u = getOtherUser(conversation);
  if (!u) return '';
  return (u.firstName?.[0] || '') + (u.lastName?.[0] || '');
}

function getConvColor(conversation: IConversation): string | undefined {
  const u = getOtherUser(conversation);
  if (!u?.firstName) return undefined;
  return getAvatarColor(u.firstName, u.lastName || '');
}
</script>

<style scoped>
.minimized-chats-container {
  position: fixed;
  bottom: 80px;
  right: 16px;
  z-index: 1000;
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: flex-end;
}

.minimized-chat-bubble {
  position: relative;
  cursor: pointer;
  border-radius: 50%;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 2px solid rgba(255, 255, 255, 0.12);
  background: rgb(var(--v-theme-surface));
}

.minimized-chat-bubble:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
}

.minimized-chat-bubble:active {
  transform: scale(0.95);
}

.minimized-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: rgb(var(--v-theme-primary));
  color: #000;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.minimized-close {
  position: absolute;
  top: -6px;
  left: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
}

.minimized-chat-bubble:hover .minimized-close {
  opacity: 1;
}

/* Bubble entrance/exit transitions */
.bubble-enter-active {
  animation: bubble-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.bubble-leave-active {
  animation: bubble-out 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.bubble-move {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes bubble-in {
  0% {
    transform: scale(0) translateY(20px);
    opacity: 0;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

@keyframes bubble-out {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0);
    opacity: 0;
  }
}
</style>
